import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  DatePicker,
  Typography,
  Space,
  message,
  Spin,
  Select,
  Tag,
  Statistic,
  Input,
  AutoComplete
} from 'antd';
import { LineChartOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import ReactApexChart from 'react-apexcharts';
import { getStockInfo, getStockName, searchStocksByName } from '../services/stockService';

// ResizeObserver 에러 방지를 위한 전역 에러 핸들러
const suppressResizeObserverError = () => {
  const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
  const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
  if (resizeObserverErr) {
    resizeObserverErr.setAttribute('style', 'display: none');
  }
  if (resizeObserverErrDiv) {
    resizeObserverErrDiv.setAttribute('style', 'display: none');
  }
};

// ResizeObserver 에러 무시
window.addEventListener('error', e => {
  if (e.message === 'ResizeObserver loop limit exceeded' || 
      e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
    const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
    if (resizeObserverErr) {
      resizeObserverErr.setAttribute('style', 'display: none');
    }
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute('style', 'display: none');
    }
  }
});

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Correlation = () => {
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, 'year'),
    moment()
  ]);
  const [stockSymbols, setStockSymbols] = useState(['005930', '131970']); // 삼성전자, 두산테스나
  const [stockNames, setStockNames] = useState({}); // 심볼별 회사명 저장
  const [stocksData, setStocksData] = useState({});
  const [loading, setLoading] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  // 주식 데이터 로드
  const loadStocksData = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error('Please select date range');
      return;
    }

    if (stockSymbols.length === 0) {
      message.error('Please add at least one stock symbol');
      return;
    }

    setLoading(true);
    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');
    
    try {
      const newStocksData = {};
      
      // 병렬로 모든 주식 데이터 가져오기
      const promises = stockSymbols.map(async (symbol) => {
        try {
          const data = await getStockInfo(symbol, startDate, endDate);
          return { symbol, data };
        } catch (error) {
          console.error(`Failed to fetch data for ${symbol}:`, error);
          return { symbol, data: [] };
        }
      });

      const results = await Promise.all(promises);
      
      results.forEach(({ symbol, data }) => {
        newStocksData[symbol] = data;
      });

      setStocksData(newStocksData);
      message.success(`Loaded data for ${stockSymbols.length} stocks`);
    } catch (error) {
      console.error('Failed to fetch stocks data', error);
      message.error('Failed to fetch stocks data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 주식명 로드
  const loadStockNames = async () => {
    const newStockNames = {};
    
    const promises = stockSymbols.map(async (symbol) => {
      try {
        const stockInfo = await getStockName(symbol);
        return { symbol, name: stockInfo.name };
      } catch (error) {
        console.error(`Failed to fetch name for ${symbol}:`, error);
        return { symbol, name: symbol }; // 실패 시 심볼을 이름으로 사용
      }
    });

    const results = await Promise.all(promises);
    
    results.forEach(({ symbol, name }) => {
      newStockNames[symbol] = name;
    });

    setStockNames(newStockNames);
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    // ResizeObserver 에러 방지
    suppressResizeObserverError();
    
    loadStockNames();
    loadStocksData();
  }, []);

  // 주식 심볼이 변경될 때마다 주식명 다시 로드
  useEffect(() => {
    if (stockSymbols.length > 0) {
      loadStockNames();
    }
  }, [stockSymbols]);

  // DatePicker 변경 핸들러 최적화
  const handleDateRangeChange = useCallback((dates) => {
    setDateRange(dates);
    // ResizeObserver 에러 방지를 위한 지연
    setTimeout(() => {
      suppressResizeObserverError();
    }, 100);
  }, []);

  // 주식 검색 함수
  const handleSearch = async (value) => {
    if (!value || value.length < 1) {
      setSearchOptions([]);
      return;
    }

    setSearching(true);
    try {
      const result = await searchStocksByName(value, 10);
      const options = result.results.map(stock => ({
        value: stock.code,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text strong>{stock.name}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {stock.code} • {stock.market}
              </Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              {stock.close && (
                <div>
                  <Text style={{ fontSize: '12px' }}>₩{stock.close.toLocaleString()}</Text>
                  {stock.change_ratio && (
                    <div>
                      <Text 
                        style={{ 
                          fontSize: '11px',
                          color: stock.change_ratio >= 0 ? '#3f8600' : '#cf1322'
                        }}
                      >
                        {stock.change_ratio >= 0 ? '+' : ''}{stock.change_ratio.toFixed(2)}%
                      </Text>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ),
        stock: stock
      }));
      setSearchOptions(options);
    } catch (error) {
      console.error('Failed to search stocks:', error);
      setSearchOptions([]);
    } finally {
      setSearching(false);
    }
  };

  // 주식 선택 함수
  const handleSelect = (value, option) => {
    if (stockSymbols.includes(value)) {
      message.error('Stock symbol already exists');
      return;
    }

    setStockSymbols([...stockSymbols, value]);
    setNewSymbol('');
    setSearchOptions([]);
  };

  // 주식 심볼 추가 (기존 방식 - 직접 입력)
  const addStockSymbol = () => {
    if (!newSymbol.trim()) {
      message.error('Please enter a stock symbol or name');
      return;
    }
    
    if (stockSymbols.includes(newSymbol.trim())) {
      message.error('Stock symbol already exists');
      return;
    }

    setStockSymbols([...stockSymbols, newSymbol.trim()]);
    setNewSymbol('');
    setSearchOptions([]);
  };

  // 주식 심볼 제거
  const removeStockSymbol = (symbolToRemove) => {
    if (stockSymbols.length <= 1) {
      message.error('At least one stock symbol is required');
      return;
    }
    
    setStockSymbols(stockSymbols.filter(symbol => symbol !== symbolToRemove));
    
    // 데이터에서도 제거
    const newStocksData = { ...stocksData };
    delete newStocksData[symbolToRemove];
    setStocksData(newStocksData);
  };

  // 차트 데이터 준비
  const prepareChartData = () => {
    const series = [];
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#54A0FF'];
    
    stockSymbols.forEach((symbol, index) => {
      const data = stocksData[symbol];
      if (data && data.length > 0) {
        const chartData = data.map(item => ({
          x: new Date(item.date).getTime(),
          y: parseFloat(item.close) || 0  // 문자열을 숫자로 변환
        }));
        
        series.push({
          name: stockNames[symbol] ? `${stockNames[symbol]} (${symbol})` : symbol,
          data: chartData,
          color: colors[index % colors.length]
        });
      }
    });
    
    return series;
  };

  // 정규화된 차트 데이터 (첫 번째 값을 0으로 기준)
  const prepareNormalizedChartData = () => {
    const series = [];
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#54A0FF'];
    
    stockSymbols.forEach((symbol, index) => {
      const data = stocksData[symbol];
      if (data && data.length > 0) {
        const basePrice = parseFloat(data[0].close) || 0;
        if (basePrice === 0) return;  // 기준 가격이 0이면 건너뜀 (0으로 나누기 방지)
        const normalizedData = data.map(item => ({
          x: new Date(item.date).getTime(),
          // toFixed()는 문자열 반환 → parseFloat()으로 반드시 숫자 변환
          y: parseFloat(((parseFloat(item.close) - basePrice) / basePrice * 100).toFixed(4))
        }));
        
        series.push({
          name: stockNames[symbol] ? `${stockNames[symbol]} (${symbol}) - Normalized` : `${symbol} (Normalized)`,
          data: normalizedData,
          color: colors[index % colors.length]
        });
      }
    });
    
    return series;
  };

  const chartOptions = {
    chart: {
      type: 'line',
      height: 400,
      toolbar: {
        show: true
      },
      zoom: {
        enabled: true
      }
    },
    title: {
      text: 'Stock Price Comparison',
      align: 'left'
    },
    xaxis: {
      type: 'datetime',
      title: {
        text: 'Date'
      }
    },
    yaxis: {
      title: {
        text: 'Price (₩)'
      },
      labels: {
        formatter: function (val) {
          return '₩' + val.toLocaleString();
        }
      }
    },
    stroke: {
      width: 2,
      curve: 'smooth'
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left'
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val) {
          return '₩' + val.toLocaleString();
        }
      }
    }
  };

  const normalizedChartOptions = {
    ...chartOptions,
    title: {
      text: 'Normalized Stock Price Comparison (Base = 0)',
      align: 'left'
    },
    yaxis: {
      title: {
        text: 'Normalized Price'
      },
      labels: {
        formatter: function (val) {
          return val.toFixed(1);
        }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val) {
          return val.toFixed(2);
        }
      }
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        {/* Header and Controls */}
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <LineChartOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#1890ff' }} />
                <Title level={2} style={{ display: 'inline', margin: 0 }}>
                  Stock Correlation Analysis
                </Title>
              </div>
              
              <Row gutter={[16, 16]} align="top">
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Text strong>Stock Symbols</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Space wrap>
                        {stockSymbols.map(symbol => (
                          <Tag
                            key={symbol}
                            closable
                            onClose={() => removeStockSymbol(symbol)}
                            color="blue"
                          >
                            {stockNames[symbol] ? `${stockNames[symbol]} (${symbol})` : symbol}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <Space.Compact style={{ width: '100%' }}>
                        <AutoComplete
                          style={{ flex: 1 }}
                          placeholder="Search by stock name or enter symbol (e.g., 삼성전자, 005930)"
                          value={newSymbol}
                          options={searchOptions}
                          onSearch={handleSearch}
                          onSelect={handleSelect}
                          onChange={setNewSymbol}
                          notFoundContent={searching ? <Spin size="small" /> : 'No stocks found'}
                          filterOption={false}
                          allowClear
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !searchOptions.length) {
                              addStockSymbol();
                            }
                          }}
                        />
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />}
                          onClick={addStockSymbol}
                        >
                          Add
                        </Button>
                      </Space.Compact>
                      <div style={{ marginTop: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          💡 Type stock name (e.g., "삼성전자") or code (e.g., "005930") to search
                        </Text>
                      </div>
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={8} md={8}>
                  <div>
                    <Text strong>Date Range</Text>
                    <RangePicker
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      format="YYYY-MM-DD"
                      style={{ width: '100%', marginTop: '4px' }}
                      getPopupContainer={(trigger) => trigger.parentElement}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <Space size="small" wrap>
                        <Button 
                          size="small" 
                          onClick={() => {
                            setDateRange([moment().subtract(1, 'month'), moment()]);
                            setTimeout(suppressResizeObserverError, 100);
                          }}
                        >
                          1M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => {
                            setDateRange([moment().subtract(3, 'months'), moment()]);
                            setTimeout(suppressResizeObserverError, 100);
                          }}
                        >
                          3M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => {
                            setDateRange([moment().subtract(6, 'months'), moment()]);
                            setTimeout(suppressResizeObserverError, 100);
                          }}
                        >
                          6M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => {
                            setDateRange([moment().subtract(1, 'year'), moment()]);
                            setTimeout(suppressResizeObserverError, 100);
                          }}
                        >
                          1Y
                        </Button>
                      </Space>
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={4} md={8}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={loadStocksData}
                      loading={loading}
                      style={{ marginTop: '20px' }}
                    >
                      Load Data
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>

        {/* Statistics */}
        <Col span={24}>
          <Card title="Stock Statistics" size="small">
            <Row gutter={16}>
              {stockSymbols.map(symbol => {
                const data = stocksData[symbol];
                if (!data || data.length === 0) return null;
                
                const latestPrice = data[data.length - 1]?.close || 0;
                const firstPrice = data[0]?.close || 0;
                const change = latestPrice - firstPrice;
                const changePercent = firstPrice > 0 ? ((change / firstPrice) * 100) : 0;
                
                return (
                  <Col key={symbol} xs={24} sm={12} md={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Text strong style={{ fontSize: '16px' }}>
                        {stockNames[symbol] ? `${stockNames[symbol]} (${symbol})` : symbol}
                      </Text>
                      <div style={{ marginTop: '8px' }}>
                        <Statistic
                          title="Latest Price"
                          value={latestPrice}
                          precision={0}
                          prefix="₩"
                          valueStyle={{ fontSize: '14px' }}
                        />
                        <div style={{ marginTop: '4px' }}>
                          <Text 
                            style={{ 
                              color: changePercent >= 0 ? '#3f8600' : '#cf1322',
                              fontSize: '12px'
                            }}
                          >
                            {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%
                          </Text>
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card>
        </Col>

        {/* Charts */}
        <Col span={24}>
          {loading ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                  <Text>Loading stock data for correlation analysis...</Text>
                </div>
              </div>
            </Card>
          ) : (
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Card title="Absolute Price Comparison">
                  <div style={{ width: '100%', height: '400px' }}>
                    <ReactApexChart
                      options={chartOptions}
                      series={prepareChartData()}
                      type="line"
                      height={400}
                      key={`chart-1-${stockSymbols.join('-')}`}
                    />
                  </div>
                </Card>
              </Col>
              
              <Col span={24}>
                <Card title="Normalized Price Comparison (Relative Performance)">
                  <div style={{ width: '100%', height: '400px' }}>
                    <ReactApexChart
                      options={normalizedChartOptions}
                      series={prepareNormalizedChartData()}
                      type="line"
                      height={400}
                      key={`chart-2-${stockSymbols.join('-')}`}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Correlation;
